import { useCallback, useEffect, useRef, useState } from 'react';

// Generates a soft, soothing ambient pad using the Web Audio API.
// No external audio file (avoids CDN/hotlink 403s) and loops forever.
export default function useAmbientAudio() {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const ensure = useCallback(() => {
    if (ctxRef.current) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();

    const master = ctx.createGain();
    master.gain.value = 0;
    master.connect(ctx.destination);

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 750;
    filter.Q.value = 0.6;
    filter.connect(master);

    // Warm minor pad: A2, E3, A3, C4
    const freqs = [110, 164.81, 220, 261.63];
    freqs.forEach((f, i) => {
      const osc = ctx.createOscillator();
      osc.type = i % 2 === 0 ? 'triangle' : 'sine';
      osc.frequency.value = f;
      osc.detune.value = (i - 1.5) * 5;

      const g = ctx.createGain();
      g.gain.value = 0.04 * (i === 0 ? 1.4 : 1);

      // slow breathing LFO
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.05 + i * 0.02;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 0.02;
      lfo.connect(lfoGain).connect(g.gain);

      osc.connect(g).connect(filter);
      osc.start();
      lfo.start();
    });

    ctxRef.current = ctx;
    masterRef.current = master;
  }, []);

  const fade = useCallback((to, dur = 2) => {
    const ctx = ctxRef.current;
    const m = masterRef.current;
    if (!ctx || !m) return;
    const now = ctx.currentTime;
    m.gain.cancelScheduledValues(now);
    m.gain.setValueAtTime(m.gain.value, now);
    m.gain.linearRampToValueAtTime(to, now + dur);
  }, []);

  const play = useCallback(() => {
    ensure();
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume();
    fade(0.5, 2.5);
    setMuted(false);
  }, [ensure, fade]);

  const stop = useCallback(() => {
    fade(0, 1);
    setMuted(true);
  }, [fade]);

  const toggle = useCallback(() => {
    if (muted) play();
    else stop();
  }, [muted, play, stop]);

  useEffect(() => {
    return () => {
      if (ctxRef.current) ctxRef.current.close?.();
    };
  }, []);

  return { muted, toggle, play, stop };
}
